import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { toDisplayError } from '../../utils/apiError';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'profile.validation.currentPasswordRequired'),
    newPassword: z
      .string()
      .min(8, 'auth.validation.passwordTooShort')
      .regex(/[A-Z]/, 'auth.validation.passwordUppercase')
      .regex(/[a-z]/, 'auth.validation.passwordLowercase')
      .regex(/[0-9]/, 'auth.validation.passwordNumber'),
    confirmPassword: z.string().min(1, 'auth.validation.confirmPasswordRequired'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'auth.validation.passwordsMustMatch',
    path: ['confirmPassword'],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

/**
 * REV-491: change-password, moved out of the standalone /profile page into the
 * Security tab of /settings. Same endpoint and validation as before — this is
 * an information-architecture change, not a behaviour change.
 */
// REV-685: named for what it contains. This tab holds the change-password
// form and nothing else; calling it "Security" promised a breadth it never
// had, and the walkthrough flagged exactly that. If genuine security content
// (sessions, MFA, audit) lands later, that is the moment to widen the name
// again - not before.
export default function PasswordTab() {
  const { t } = useTranslation();
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onChangePassword = async (data: PasswordFormData) => {
    setPasswordError(null);
    setPasswordChanged(false);
    setSavingPassword(true);
    try {
      await apiClient.post('/v1/users/me/password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setPasswordChanged(true);
      passwordForm.reset();
    } catch (error: unknown) {
      setPasswordError(toDisplayError(error, t('profile.errors.passwordFailed')));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {t('profile.password.title')}
      </Typography>
      {passwordChanged && (
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          {t('profile.password.changed')}
        </Alert>
      )}
      {passwordError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {passwordError}
        </Alert>
      )}
      <Box
        component="form"
        onSubmit={passwordForm.handleSubmit(onChangePassword)}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          {...passwordForm.register('currentPassword')}
          label={t('profile.password.current')}
          type={showCurrent ? 'text' : 'password'}
          autoComplete="current-password"
          error={!!passwordForm.formState.errors.currentPassword}
          helperText={
            passwordForm.formState.errors.currentPassword
              ? t(passwordForm.formState.errors.currentPassword.message as string)
              : undefined
          }
          disabled={savingPassword}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showCurrent ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowCurrent((p) => !p)}
                    edge="end"
                  >
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          {...passwordForm.register('newPassword')}
          label={t('profile.password.new')}
          type={showNew ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!passwordForm.formState.errors.newPassword}
          helperText={
            passwordForm.formState.errors.newPassword
              ? t(passwordForm.formState.errors.newPassword.message as string)
              : t('profile.password.hint')
          }
          disabled={savingPassword}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showNew ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowNew((p) => !p)}
                    edge="end"
                  >
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          {...passwordForm.register('confirmPassword')}
          label={t('profile.password.confirm')}
          type={showNew ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!passwordForm.formState.errors.confirmPassword}
          helperText={
            passwordForm.formState.errors.confirmPassword
              ? t(passwordForm.formState.errors.confirmPassword.message as string)
              : undefined
          }
          disabled={savingPassword}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Divider />
        <Box>
          <Button type="submit" variant="contained" disabled={savingPassword}>
            {savingPassword ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('profile.password.submit')
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
